import { useCallback } from 'react'
import { useAppDispatch } from '@/state'
import { GetBonusesPayload } from '@/state/bonus-engine/models'

import { FormStatusType } from '@/enums/form-status.enum'
import { setFormModeAction } from '@/state/ui/global/slices/index.slices'
import { WalletActions } from '@/state/wallets/slices/index.slices'

import {
  GetWalletsPayload,
  DeleteWalletPayload,
  ManualBonusPayload,
  WalletFilters,
  WalletLibraryColumnProps,
  Wallets,
  CalculateRolloverPayload,
  SyncBetsoftBonusPayload,
} from '@/state/wallets/models'
import deleteWalletThunk from '@/state/wallets/thunks/delete.thunk'
import { getAllWalletThunk } from '@/state/wallets/thunks/get-all.thunk'

import { setManualBonusThunk } from '@/state/wallets/thunks/set-manual-bonus.thunk'
import { getAllBonusesThunk } from '@/state/bonus-engine/thunks/get-all.thunk'
import { TableHeadCellPropsType } from '@/state/ui/global/models'

import { exportXlsxFile, removeEmptyProp } from '@/utils'
import { calculateRolloverThunk } from '@/state/wallets/thunks/calculate-rollover.thunk'
import { syncBetsoftBonusThunk } from '@/state/wallets/thunks/sync-betsoft-bonus.thunk'

import { getBonusBalanceThunk } from '@/state/wallets/thunks/get-bonus-balance.thunk'

export const useWalletsActions = () => {
  const dispatch = useAppDispatch()

  const getAllWallets = async (
    filters: GetWalletsPayload,
    headCells?: TableHeadCellPropsType<WalletLibraryColumnProps>[],
    fileName?: string,
    getFormattedRow?: (row: Wallets) => WalletLibraryColumnProps,
  ) => {
    const newFilters = removeEmptyProp(filters)

    const resultAction = await dispatch(getAllWalletThunk(newFilters))

    if (
      getFormattedRow &&
      headCells &&
      getAllWalletThunk.fulfilled.match(resultAction)
    ) {
      exportXlsxFile(
        headCells,
        resultAction.payload.docs.map((item) => getFormattedRow(item)),
        fileName,
      )
    }
  }

  const getAllBonuses = useCallback(
    (payload: GetBonusesPayload) => {
      dispatch(
        getAllBonusesThunk({
          ...payload,
        }),
      )
    },
    [dispatch], // eslint-disable-line
  )

  const setManualBonus = useCallback(
    (payload: ManualBonusPayload) => dispatch(setManualBonusThunk(payload)),
    [dispatch],
  )

  const deleteWallet = useCallback(
    (payload: DeleteWalletPayload) => dispatch(deleteWalletThunk(payload)),
    [dispatch],
  )

  const calculateRollover = useCallback(
    (payload: CalculateRolloverPayload) =>
      dispatch(calculateRolloverThunk(payload)),
    [dispatch],
  )

  const syncBetsoftBonus = useCallback(
    (payload: SyncBetsoftBonusPayload) =>
      dispatch(syncBetsoftBonusThunk(payload)),
    [dispatch],
  )

  const setFormType = useCallback(
    (formMode: FormStatusType) => dispatch(setFormModeAction(formMode)),
    [dispatch],
  )

  const setWalletsFilters = useCallback(
    (filters: WalletFilters) =>
      dispatch(WalletActions.setWalletFiltersAction(filters)),
    [dispatch],
  )

  const getWalletBonusBalance = useCallback(
    (payload: { playerId: string }) => dispatch(getBonusBalanceThunk(payload)),
    [dispatch],
  )

  return {
    getAllBonuses,
    getAllWallets,
    setFormType,
    setWalletsFilters,
    deleteWallet,
    setManualBonus,
    calculateRollover,
    syncBetsoftBonus,
    getWalletBonusBalance,
  }
}
